import { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContext";
import { SectionHead } from "@/components/SectionHead";
import { DEFAULT_IMAGES } from "@/config";

export const Gallery = () => {
  const { t, isAr } = useLang();
  const { img } = useSite();
  const g = t.gallery;
  const track = useRef(null);

  const items = DEFAULT_IMAGES.gallery.map((fallback, i) => ({ src: img(`img_gallery_${i + 1}`, fallback) }));

  const scroll = (dir) => {
    if (!track.current) return;
    const d = isAr ? -1 : 1;
    track.current.scrollBy({ left: d * dir * 460, behavior: "smooth" });
  };

  return (
    <section id="gallery" className="bg-creamdeep/60 py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-end justify-between gap-6">
          <SectionHead kicker={g.kicker} title={g.title} sub={g.sub} />
          <div className="hidden shrink-0 gap-3 md:flex">
            <button
              onClick={() => scroll(-1)}
              className="grid h-12 w-12 place-items-center rounded-full border border-line bg-white text-pine transition-all hover:border-pine"
              aria-label="Previous photos"
              data-testid="gallery-prev-button"
            >
              <ChevronLeft className="h-5 w-5 rtl:rotate-180" />
            </button>
            <button
              onClick={() => scroll(1)}
              className="grid h-12 w-12 place-items-center rounded-full border border-line bg-white text-pine transition-all hover:border-pine"
              aria-label="Next photos"
              data-testid="gallery-next-button"
            >
              <ChevronRight className="h-5 w-5 rtl:rotate-180" />
            </button>
          </div>
        </div>

        <div ref={track} className="no-scrollbar mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-2" data-testid="gallery-track">
          {items.map((item, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
              className="group relative h-[300px] w-[82vw] shrink-0 snap-start overflow-hidden rounded-[1.8rem] border border-cardline sm:h-[340px] sm:w-[440px]"
              data-testid={`gallery-item-${i}`}
            >
              <img src={item.src} alt={g.captions[i]} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-pine/85 via-pine/30 to-transparent p-5 pt-14 text-sm font-bold text-cream">
                {g.captions[i]}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
